"use client";

import { FirebaseError } from "firebase/app";
import { auth } from "../firebaseConfig";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { createSessionAction } from "./createSessionCookie";

export type SignInError = {
  errorCode: string;
  message: string;
  /** エラーの原因が特定できた入力欄。該当欄を赤枠にするために使う */
  field?: "email" | "password";
};

/**
 * Firebase の認証エラーコードを、原因の分かる日本語メッセージに割り当てる。
 *
 * 注意: 「メールアドレスが存在しない」か「パスワードが違う」かは区別しない。
 * Firebase の Email Enumeration Protection により両者とも auth/invalid-credential が
 * 返るうえ、区別して返すこと自体がアカウントの存在を推測させる（アカウント列挙攻撃）ため。
 * 形式不正・アカウント無効・試行回数超過など、列挙に繋がらないものだけ具体化する。
 */
function toSignInError(errorCode: string): SignInError {
  switch (errorCode) {
    case "auth/invalid-email":
      return { errorCode, field: "email", message: "メールアドレスの形式が正しくありません" };
    case "auth/missing-email":
      return { errorCode, field: "email", message: "メールアドレスを入力してください" };
    case "auth/missing-password":
    case "auth/weak-password":
      return { errorCode, field: "password", message: "パスワードを入力してください" };
    case "auth/user-disabled":
      return {
        errorCode,
        field: "email",
        message: "このアカウントは利用停止されています。運営までお問い合わせください",
      };
    case "auth/too-many-requests":
      return {
        errorCode,
        message:
          "ログインの試行回数が多いため、一時的にロックされています。しばらく待ってからお試しください",
      };
    case "auth/network-request-failed":
      return { errorCode, message: "通信に失敗しました。ネットワーク環境をご確認ください" };
    default:
      // auth/invalid-credential / auth/wrong-password / auth/user-not-found など
      return { errorCode, message: "メールアドレスまたはパスワードが正しくありません" };
  }
}

// client側でサインインし、idTokenを取得し、サーバーでidTokenからセッションcookieを作成する
export const signIn = async (email: string, password: string): Promise<SignInError | null> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const idToken = await userCredential.user.getIdToken();
    await userCredential.user.getIdTokenResult();
    // サーバー側でセッションcookieを作成するためにidTokenを返す。
    // 停止・削除されたアカウント（解約済みのエージェントなど）にはサーバーがセッションを出さない
    const session = await createSessionAction(idToken);
    // sessionCookieを使うのでクライアント側でのログインは不要
    await signOut(auth);

    if (!session.ok) {
      return {
        errorCode: "byagent/account-inactive",
        message: "このアカウントはご利用いただけません。運営までお問い合わせください",
      };
    }
    return null;
  } catch (error) {
    const firebaseError = error as FirebaseError;
    return toSignInError(firebaseError.code);
  }
};
